import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "moving-company-web-design-colorado",
  metaTitle: "Moving Company Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom moving company web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Moving company marketing", to: "/moving-company-marketing-colorado" },
  ],
  currentLabel: "Moving Company Web Design Colorado",
  eyebrow: "Moving Company Web Design — Colorado",
  h1: (
    <>
      Moving Company <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded moving company websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Moving Company websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first moving company sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Moving Company web design in Colorado",
    body: "Moving Company web design is the practice of building conversion-focused, mobile-first websites for moving company companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with instant quote calculators above the fold." },
    { title: "Instant quote calculators", body: "Bedroom/distance quote calculators that book in under 60 seconds." },
    { title: "Service pages per move type", body: "Local, long-distance, commercial, packing, piano, senior — each ranks independently." },
    { title: "Booking + scheduling integration", body: "Integration with SmartMoving, MoversSuite, or your CRM for real-time booking." },
    { title: "Trust signals above the fold", body: "DOT/MC numbers, insurance, BBB rating, Google reviews surfaced where movers actually look." },
    { title: "Schema and SEO baked in", body: "MovingCompany, Service, FAQPage, Review schema at launch." },
  ],
  whyThisMarket: [
    { heading: "Trust signals decide the booking", body: "Movers are letting strangers handle their entire life. DOT/MC, insurance, and reviews must be above the fold." },
    { heading: "Instant quote beats form-fill 4:1", body: "Movers shop fast and book fast. Sites with instant quote calculators convert 3–4x higher." },
    { heading: "Long-distance and local are different searches", body: "Different intent, different pricing, different SEO. They deserve different landing pages." },
  ],
  relatedLinks: [
    { to: "/moving-company-marketing-colorado", label: "Moving company marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado moving company case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a moving company website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a moving company website?", a: "Typical timeline is 4–6 weeks for a 12–20 page moving company site with full service and service-area pages. Larger multi-location moving company companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs moving company companies?", a: "No — we build moving company sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Moving Company Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for moving company companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/moving-company-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
